/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { type AxiosInstance } from 'axios';
import { getAxios } from '@/lib/axios/index';
import { selector as getMyProfileSelector, myProfileQuery } from './getMyProfile';
import { type MyProfileData, type MyProfileUpdateParams } from '../interface';
import { requestBaseURL } from '@/config/serviceAPI';
import { useRouter } from 'next/router';

const URL = `${requestBaseURL}/ecomm/b2c/v1/currentUser`;

const updateMyProfile = async (
  props: Partial<MyProfileUpdateParams> | null | undefined,
  axiosInstance?: AxiosInstance
) => {
  const response = await getAxios(axiosInstance).patch<MyProfileData>(URL, props, {
    headers: { Accept: 'application/json' },
  });

  return getMyProfileSelector(response.data);
};

export const useUpdateMyProfile = ({
  onError,
  onSuccess: customOnSuccess,
}: { onError?: (err: unknown) => void; onSuccess?: (data: MyProfileData) => void } = {}) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const isCheckout = router.pathname === '/checkout';
  const data = queryClient.getQueryData(myProfileQuery.getFullKey({}));
  // sometimes loyaltyAccountAlerts is set to null, and that is falsey,
  // and that's a problem because we were checking if it existed based on truthiness,
  // regardless we don't want to send it to the backend,
  // so my solution create it with a dummy value and then immediately delete it
  if (data && !isCheckout) {
    // @ts-expect-error CB-8438 loyaltyAccountAlerts is not a valid property
    data.loyaltyAccountAlerts = 'false';
    // @ts-expect-error CB-8438 loyaltyAccountAlerts is not a valid property
    delete data.loyaltyAccountAlerts;
  }

  return useMutation({
    mutationFn: updateMyProfile,
    onSuccess: (data: MyProfileData) => {
      queryClient.setQueryData(myProfileQuery.getFullKey({}), () => data);
      customOnSuccess?.(data);
    },
    onError,
  });
};
